/**
 * Os pinouts a que uma linha da tabela de especificações se refere.
 *
 * **Quem lê este vínculo é o cliente, não o Sistema.** No site, abrir um
 * componente no diagrama interativo lista as tabelas de especificação ligadas a
 * ele — o caminho é `diagrama_componente → diagrama_ligacao → …
 * → tabela_linha_pinout → tabela`. Sem o vínculo a tabela existe e ninguém a
 * encontra por ali.
 *
 * São **55 vínculos em 32 das 18.417 linhas**, e é pouco porque a única tela que
 * os criava estava no sistema antigo.
 *
 * **O candidato não é qualquer pinout.** A regra exige que a restrição do pinout
 * alcance um modelo cujo veículo e faixa de anos estejam entre os da tabela — os
 * pinouts dos veículos que esta tabela cobre. É o que impede ligar uma linha de
 * torque ao pinout de um carro que ela não descreve.
 */

import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "@/api/cliente"
import { Botao, Linha, mensagem } from "./pecas"

type PinoutDaLinha = {
  pinoutId: number
  projetoId: number
  projeto: string
  componenteId: number
  componente: string
  conector: string
  pino: string
}

type Candidatos = {
  total: number
  pinouts: PinoutDaLinha[]
  projetos: { id: number; nome: string }[]
  componentes: { id: number; nome: string }[]
}

/** Quantos desenhar. O universo chega a centenas — 420 numa linha medida. */
const DESENHADOS = 120

export function PinoutsDaLinha({ tabelaId, linhaId, aoAvisar }: {
  tabelaId: number
  linhaId: number
  aoAvisar: (m: string | null) => void
}) {
  const qc = useQueryClient()
  const base = `api/tabelas-especificacao/${tabelaId}/linhas/${linhaId}/pinouts`

  const [ligando, setLigando] = useState(false)
  const [projeto, setProjeto] = useState(0)
  const [componente, setComponente] = useState(0)

  const ligados = useQuery({
    queryKey: ["linha-pinouts", linhaId] as const,
    queryFn: () => obter<PinoutDaLinha[]>(base, {}),
  })

  const candidatos = useQuery({
    queryKey: ["linha-pinouts-candidatos", linhaId, projeto, componente] as const,
    enabled: ligando,
    queryFn: () => obter<Candidatos>(`${base}/candidatos`, {
      projetoId: projeto || undefined,
      componenteId: componente || undefined,
    }),
  })

  const gravar = useMutation({
    mutationFn: async ({ pinoutId, ligar }: { pinoutId: number; ligar: boolean }) => {
      const url = `${base}/${pinoutId}`
      return ligar
        ? (await api.post(url, null, foraDoProjeto())).data
        : (await api.delete(url, foraDoProjeto())).data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["linha-pinouts", linhaId] }),
  })

  function mexer(pinoutId: number, ligar: boolean) {
    aoAvisar(null)
    gravar.mutate({ pinoutId, ligar }, {
      onSuccess: () => aoAvisar(ligar ? "Pinout ligado à linha." : "Pinout desligado."),
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  const jaLigados = new Set((ligados.data ?? []).map((p) => p.pinoutId))

  return (
    <div style={{
      marginTop: 10, padding: 11, borderRadius: 6,
      border: "1px solid var(--line2)", background: "var(--surface2)",
    }}>
      <p style={{ margin: "0 0 7px", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5 }}>
        No site, quem abre este componente no diagrama interativo vê esta tabela.{" "}
        <b>Sem vínculo, ela não aparece por ali.</b>
      </p>

      {ligados.isPending ? (
        <p style={vazio}>Carregando…</p>
      ) : (ligados.data ?? []).length === 0 ? (
        <p style={vazio}>Esta linha não está ligada a pinout nenhum.</p>
      ) : (
        <Tabela
          itens={ligados.data ?? []}
          acao={(p) => (
            <button onClick={() => mexer(p.pinoutId, false)} title="Desligar" style={botaoX}>×</button>
          )}
        />
      )}

      <div style={{ marginTop: 9 }}>
        {!ligando ? (
          <Botao aoClicar={() => setLigando(true)}>Ligar a um pinout…</Botao>
        ) : (
          <>
            <Linha>
              <select
                value={projeto}
                onChange={(e) => { setProjeto(Number(e.target.value)); setComponente(0) }}
                style={seletor}
              >
                <option value={0}>Todos os projetos</option>
                {(candidatos.data?.projetos ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.nome || `Projeto ${p.id}`}</option>
                ))}
              </select>
              <select
                value={componente}
                onChange={(e) => setComponente(Number(e.target.value))}
                style={seletor}
              >
                <option value={0}>Todos os componentes</option>
                {(candidatos.data?.componentes ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.nome || `Componente ${c.id}`}</option>
                ))}
              </select>
              <Botao aoClicar={() => setLigando(false)}>Fechar</Botao>
            </Linha>

            {candidatos.isPending ? (
              <p style={vazio}>Procurando os pinouts desta tabela…</p>
            ) : candidatos.data && candidatos.data.total === 0 ? (
              <p style={{ ...vazio, marginTop: 7 }}>
                Nenhum pinout alcança os veículos desta tabela. O vínculo só aceita pinout de
                veículo que a tabela cobre.
              </p>
            ) : (
              <div style={{ marginTop: 7 }}>
                <p style={{ margin: "0 0 5px", fontSize: 10.5, color: "var(--txt3)" }}>
                  {candidatos.data?.pinouts.length.toLocaleString("pt-BR")} de{" "}
                  {candidatos.data?.total.toLocaleString("pt-BR")} candidatos
                  {(candidatos.data?.pinouts.length ?? 0) > DESENHADOS &&
                    ` · mostrando ${DESENHADOS}`}
                </p>
                <div style={{ maxHeight: 220, overflowY: "auto" }}>
                  <Tabela
                    itens={(candidatos.data?.pinouts ?? []).slice(0, DESENHADOS)}
                    acao={(p) => jaLigados.has(p.pinoutId)
                      ? <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>ligado</span>
                      : <Botao aoClicar={() => mexer(p.pinoutId, true)}>Ligar</Botao>}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function Tabela({ itens, acao }: {
  itens: PinoutDaLinha[]
  acao: (p: PinoutDaLinha) => React.ReactNode
}) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
      <tbody>
        {itens.map((p) => (
          <tr key={p.pinoutId} style={{ borderBottom: "1px solid var(--line2)" }}>
            <td style={{ ...cel, width: 200 }} title={p.projeto}>
              <span style={corte}>{p.projeto || `Projeto ${p.projetoId}`}</span>
            </td>
            <td style={cel} title={p.componente}>
              <span style={corte}>{p.componente || `Componente ${p.componenteId}`}</span>
            </td>
            <td style={{ ...cel, width: 110 }} title={p.conector}>
              <span style={corte}>{p.conector}</span>
            </td>
            <td style={{ ...cel, width: 60 }} className="mono">{p.pino}</td>
            <td style={{ ...cel, width: 64, textAlign: "right" }}>{acao(p)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const cel: React.CSSProperties = { padding: "4px 7px", verticalAlign: "middle" }
const corte: React.CSSProperties = {
  display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
}
const vazio: React.CSSProperties = { margin: 0, fontSize: 11.5, color: "var(--txt3)" }
const seletor: React.CSSProperties = {
  padding: "4px 7px", fontSize: 11.5, maxWidth: 240,
  border: "1px solid var(--line)", borderRadius: 5,
  background: "var(--surface)", color: "var(--txt)",
}
const botaoX: React.CSSProperties = {
  border: 0, background: "transparent", cursor: "pointer",
  color: "var(--txt3)", fontSize: 13, lineHeight: 1,
}
